import { decodeJwtPayload } from "../utils/jwtRole";
import {
  clearAuthStorage,
  getAuthItem,
  setAuthItem,
} from "../utils/authStorage";

const SESSION_TIMEOUT_PATH = "/session-timeout";

let redirecting = false;

export function clearSessionStorage() {
  clearAuthStorage();
}

export function isJwtExpired(token) {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp)) return false;
  return exp * 1000 <= Date.now();
}

/** Remove expired JWT from storage without redirecting (anonymous / open screens). */
export function discardExpiredToken() {
  if (typeof window === "undefined") return false;
  const token = getAuthItem("jwt_token") || getAuthItem("token");
  if (!token || !isJwtExpired(token)) return false;
  clearSessionStorage();
  return true;
}

/** Redirect to session timeout page (no API error message shown). */
export function redirectToSessionTimeout() {
  if (typeof window === "undefined") return;
  if (redirecting) return;
  if (window.location.pathname === SESSION_TIMEOUT_PATH) return;
  redirecting = true;
  clearSessionStorage();
  window.location.replace(SESSION_TIMEOUT_PATH);
}

export function getValidJwtToken() {
  if (typeof window === "undefined") return null;
  const token = getAuthItem("jwt_token");
  if (!token) return null;
  if (isJwtExpired(token)) {
    clearSessionStorage();
    return null;
  }
  return token;
}

export function buildAuthHeaders(extra = {}) {
  const token = getValidJwtToken();
  const csrf = getAuthItem("csrf_token") || getAuthItem("csrf-token");
  const headers = {
    "Content-Type": "application/json",
    ...extra,
  };
  if (csrf) headers["X-CSRF-Token"] = csrf;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Like buildAuthHeaders but never redirects when JWT is expired (open / dev screens). */
export function buildAuthHeadersSoft(extra = {}) {
  if (typeof window === "undefined") {
    return { "Content-Type": "application/json", ...extra };
  }
  const raw = getAuthItem("jwt_token");
  const token = raw && !isJwtExpired(raw) ? raw : null;
  const csrf = getAuthItem("csrf_token") || getAuthItem("csrf-token");
  const headers = {
    "Content-Type": "application/json",
    ...extra,
  };
  if (csrf) headers["X-CSRF-Token"] = csrf;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Fetch for open screens: never sends you to /session-timeout on expired JWT or 401.
 * Surfaces errors to the caller instead (show message on the page).
 */
export async function openScreenFetch(input, init = {}) {
  const headers = {
    ...buildAuthHeadersSoft(),
    ...(init.headers || {}),
  };
  // FormData must not use application/json — browser sets multipart boundary.
  if (init.body instanceof FormData) {
    delete headers["Content-Type"];
  }

  return fetch(input, {
    ...init,
    headers,
    credentials: init.credentials ?? "include",
  });
}

/**
 * Authenticated fetch: expired JWT or 401 → session timeout page (no thrown user message).
 */
export async function authFetch(input, init = {}) {
  discardExpiredToken();

  const isFormData =
    typeof FormData !== "undefined" && init.body instanceof FormData;

  const headers = {
    ...buildAuthHeaders(),
    ...(init.headers || {}),
  };
  // FormData must not use application/json — browser sets multipart boundary.
  if (init.body instanceof FormData) {
    delete headers["Content-Type"];
  }

  if (init.body instanceof FormData) {
    delete headers["Content-Type"];
  }

  /* Let the browser set multipart boundary — do not send application/json. */
  if (isFormData) {
    delete headers["Content-Type"];
  }

  const res = await fetch(input, {
    ...init,
    headers,
    credentials: init.credentials ?? "include",
  });

  if (res.status === 401) {
    let forceLogout = true;
    try {
      const data = await res.clone().json();
      const msg = String(data?.message || "").toLowerCase();
      /* Backend sometimes used 401 for business/setup errors — do not clear session. */
      if (
        msg.includes("not linked") ||
        msg.includes("could not record") ||
        msg.includes("could not determine") ||
        msg.includes("please correct")
      ) {
        forceLogout = false;
      }
    } catch {
      /* Opaque 401: treat as session failure. */
    }
    if (forceLogout) {
      redirectToSessionTimeout();
      const err = new Error("session_timeout");
      err.status = 401;
      throw err;
    }
  }

  return res;
}

/** Axios apps: expired JWT or 401 → session timeout page. */
export function attachSessionInterceptors(axiosInstance) {
  axiosInstance.interceptors.request.use((requestConfig) => {
    discardExpiredToken();
    const token = getAuthItem("jwt_token") || getAuthItem("token");
    if (token && !isJwtExpired(token)) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    /* Cross-origin API (e.g. localhost:3000 → :5000): send CSRF cookie + header for mutating requests. */
    requestConfig.withCredentials = true;

    const method = String(requestConfig.method || "get").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const csrf = getAuthItem("csrf_token") || getAuthItem("csrf-token");
      if (csrf && !requestConfig.headers["X-CSRF-Token"]) {
        requestConfig.headers["X-CSRF-Token"] = csrf;
      }
    }

    return requestConfig;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        const hadToken = Boolean(getAuthItem("jwt_token") || getAuthItem("token"));
        if (hadToken) {
          redirectToSessionTimeout();
          return new Promise(() => {});
        }
      }
      return Promise.reject(error);
    },
  );

  return axiosInstance;
}
